/**
 * M9 CH-5b: a hulk's kit (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §2, §4.3; economy direction §2.7).
 * Start, deliver, abandon and complete the kit that makes a surveyed hulk flightworthy, persisted on the Career in
 * `career.hulks.sites[key]` (`open`, `kit`).
 *
 * - **The requirement** is CH-4's `hulkKit(site.hull)`: ceil(`kit.share` × each `buildCost` ware) plus fuel cells.
 *   Goods only: no credits, no tetherstone (a `GoodId` is never a raw ware). No Rush bonus: delivery runs the shared
 *   rule (`kits/deliver.ts`) with `rushFuelCellsPerPart` 0.
 * - **Start** (`startKit`) needs a surveyed site of a claimable hull that is not finished, and fewer than
 *   `fleet.json claim.maxOpen` open claims on the Career (the local mirror of the worker's `markClaim` cap, MS-1).
 *   It opens the claim with an empty kit; starting an open kit again changes nothing.
 * - **Deliver** (`deliver`) takes every missing good the hold carries, in whole units, and only what is missing. It
 *   is refused until the hulk is secured (CH-6 `hulkClearRefusal`: a coolant hazard vented, squatters cleared).
 * - **Stand-ins** (`standIn`, SY-1/D-362): a salvaged component at Serviceable or better (never Wrecked) stands in
 *   for the goods it breaks down into (`modules.json salvageYields`, at full yield), crediting only what is still
 *   missing; it is consumed whole and only when it fills something. Contract, story, sealed and contraband parts and
 *   a part an open fab order holds never stand in. One named part per call: the hold-wide `deliver` never spends a
 *   component the pilot may want to keep.
 * - **Abandon** (`abandon`): the claim closes and the delivered goods are lost, by design (the risk). The site and
 *   its survey stay, so the claim can be opened again from nothing.
 * - **Done** (`kitDone`): the claim is open and every good is in; CH-7s then registers the ship (CH-7a).
 *
 * Career in, Career out; pure. UI-rate, never per frame.
 */
import { BREAKDOWN_YIELDS, type BreakDownYields } from '../career/breakdown'
import { berthRefusal } from '../career/berth'
import { withoutComponent } from '../career/ops'
import type { GoodId } from '../career/parts'
import type { Career, HulkKit, HulkSite, HulkState } from '../career/types'
import fleetJson from '../data/strategic/fleet.json'
import {
  acceptsComponent,
  deliverGoodsAndParts,
  deliverItem,
  kitComplete,
  kitMissing,
  SALVAGE_BANDS,
  type KitProgress,
  type KitRequirement,
  type SalvageBands,
} from '../kits/deliver'
import { fleetConfigSchema } from '../strategic/schemas'
import { hulksConfig, type HulksConfig } from './data'
import { hulkClearRefusal } from './secure'
import { hulkEligible, hulkKit, type HulkRef } from './survey'

let cachedMaxOpen: number | null = null
/** `fleet.json claim.maxOpen`: the most open claims at once (the worker's `markClaim` cap, MS-1). */
export function claimMaxOpen(): number {
  cachedMaxOpen ??= fleetConfigSchema.parse(fleetJson).claim.maxOpen
  return cachedMaxOpen
}

export type HulkKitRefusal =
  /** No site for the key (never found or surveyed), or the hulk is finished (`done`). */
  | 'noSite'
  /** Found but not surveyed: the kit list comes from the survey. */
  | 'notSurveyed'
  /** The hull is not claimable (`hulkEligible`). */
  | 'notEligible'
  /** `claimMaxOpen()` claims are open already. */
  | 'tooManyOpen'
  /** No open claim on this hulk. */
  | 'notOpen'
  /** The coolant hazard is not vented yet (CH-6 `hulkClearRefusal`). */
  | 'hazard'
  /** The squatters are not cleared yet (CH-6). */
  | 'squatters'
  /** Every good is in already. */
  | 'complete'
  /** The hold carries nothing the kit still needs. */
  | 'nothingToDeliver'
  /** The named component is not in the hold. */
  | 'notInHold'
  /** The component is below Serviceable (SY-1). */
  | 'wrecked'
  /** Contract, story, sealed or contraband cargo, or a part an open fab order holds. */
  | 'heldBack'
  /** The component breaks down into nothing the kit still needs. */
  | 'notNeeded'

export type HulkKitFail = { ok: false; reason: HulkKitRefusal }

export type HulkKitResult = { ok: true; career: Career } | HulkKitFail

export type HulkDeliverResult =
  | {
      ok: true
      career: Career
      /** Units of each good taken (from the hold, or credited by a stand-in component). */
      goods: Partial<Record<GoodId, number>>
      /** The component a stand-in consumed (`standIn` only). */
      componentId?: string
      /** Every good is in now (`kitDone`). */
      done: boolean
    }
  | HulkKitFail

export interface HulkKitOptions {
  cfg?: HulksConfig
  bands?: SalvageBands
  yields?: BreakDownYields
}

const NO_HULKS: HulkState = { done: [], sites: {} }
const EMPTY_KIT = (): HulkKit => ({ components: {}, goods: {} })

const hulksOf = (career: Career): HulkState => career.hulks ?? NO_HULKS

/** The site of an unfinished hulk, or undefined. */
function liveSite(career: Career, key: string): HulkSite | undefined {
  const state = hulksOf(career)
  if (state.done.includes(key)) return undefined
  return Object.hasOwn(state.sites, key) ? state.sites[key] : undefined
}

function withSite(career: Career, key: string, site: HulkSite): Career {
  const state = hulksOf(career)
  return { ...career, hulks: { done: state.done, sites: { ...state.sites, [key]: site } } }
}

/** The kit's progress in the shared rule's shape (only the positive entries). */
function progressOf(kit: HulkKit | undefined): KitProgress {
  const goods: Record<string, number> = {}
  for (const [g, n] of Object.entries(kit?.goods ?? {})) if (n !== undefined && n > 0) goods[g] = n
  return { components: { ...(kit?.components ?? {}) }, goods }
}

function kitOf(progress: KitProgress): HulkKit {
  return { components: { ...progress.components }, goods: { ...progress.goods } }
}

/** The open claims on the Career (seeded and off-screen hulks alike). */
export function openHulkCount(career: Career): number {
  let n = 0
  for (const site of Object.values(hulksOf(career).sites)) if (site.open === true) n++
  return n
}

/** What the hulk's kit needs (CH-4's `hulkKit` of the surveyed hull). */
export function hulkKitRequirement(site: Pick<HulkSite, 'hull'>, cfg: HulksConfig = hulksConfig()): KitRequirement {
  return hulkKit(site.hull, cfg)
}

/** What the hulk's kit still misses (the whole kit when none is started; nothing for an unknown key). */
export function hulkKitMissing(career: Career, key: string, cfg: HulksConfig = hulksConfig()): KitRequirement {
  const site = liveSite(career, key)
  if (site === undefined) return { components: {}, goods: {} }
  return kitMissing(hulkKitRequirement(site, cfg), progressOf(site.kit))
}

/** The claim is open and every good is in: CH-7s may register the ship. */
export function kitDone(career: Career, key: string, cfg: HulksConfig = hulksConfig()): boolean {
  const site = liveSite(career, key)
  if (site?.open !== true || site.kit === undefined) return false
  return kitComplete(hulkKitRequirement(site, cfg), progressOf(site.kit))
}

/**
 * Opens the claim on a surveyed hulk with an empty kit. Refused for an unknown or finished hulk, one not surveyed,
 * a hull that isn't claimable, or at `maxOpen` open claims. Starting an open kit again returns the Career unchanged.
 */
export function startKit(career: Career, key: string, maxOpen: number = claimMaxOpen(), cfg: HulksConfig = hulksConfig()): HulkKitResult {
  const site = liveSite(career, key)
  if (site === undefined) return { ok: false, reason: 'noSite' }
  if (site.surveyed === undefined) return { ok: false, reason: 'notSurveyed' }
  if (!hulkEligible(site.hull, cfg)) return { ok: false, reason: 'notEligible' }
  if (site.open === true) return { ok: true, career }
  if (openHulkCount(career) >= maxOpen) return { ok: false, reason: 'tooManyOpen' }
  return { ok: true, career: withSite(career, key, { ...site, open: true, kit: EMPTY_KIT() }) }
}

/** The open, secured (CH-6 `hulkClearRefusal`), unfinished kit at `ref.key`, or why not. */
function openKit(career: Career, ref: HulkRef, cfg: HulksConfig): { site: HulkSite; req: KitRequirement; progress: KitProgress } | HulkKitFail {
  const site = liveSite(career, ref.key)
  if (site === undefined) return { ok: false, reason: 'noSite' }
  if (site.open !== true) return { ok: false, reason: 'notOpen' }
  const unsafe = hulkClearRefusal(career, ref, cfg)
  if (unsafe !== null) return { ok: false, reason: unsafe }
  const req = hulkKitRequirement(site, cfg)
  const progress = progressOf(site.kit)
  if (kitComplete(req, progress)) return { ok: false, reason: 'complete' }
  return { site, req, progress }
}

/**
 * Delivers every missing good the hold carries to the open kit at `ref.key` (whole units, never more than is
 * missing; the hold loses only what was taken). Components are never taken here (see `standIn`).
 */
export function deliver(career: Career, ref: HulkRef, opts: HulkKitOptions = {}): HulkDeliverResult {
  const cfg = opts.cfg ?? hulksConfig()
  const k = openKit(career, ref, cfg)
  if ('ok' in k) return k
  const r = deliverGoodsAndParts(k.req, k.progress, career, { bands: opts.bands ?? SALVAGE_BANDS, rushFuelCellsPerPart: 0 })
  if (Object.keys(r.taken.goods).length === 0) return { ok: false, reason: 'nothingToDeliver' }
  const next = withSite(r.career, ref.key, { ...k.site, kit: kitOf(r.delivered) })
  return { ok: true, career: next, goods: r.taken.goods, done: kitComplete(k.req, r.delivered) }
}

/**
 * One salvaged component from the hold stands in for the goods it breaks down into (full yield, SY-1): it must be
 * Serviceable or better, free cargo (not contract, story, sealed, contraband or held by an open fab order), and fill
 * at least one missing unit. It is consumed whole; only the missing units are credited.
 */
export function standIn(career: Career, ref: HulkRef, componentId: string, opts: HulkKitOptions = {}): HulkDeliverResult {
  const cfg = opts.cfg ?? hulksConfig()
  const k = openKit(career, ref, cfg)
  if ('ok' in k) return k
  const part = career.cargo.components.find((c) => c.id === componentId)
  if (part === undefined) return { ok: false, reason: 'notInHold' }
  if (berthRefusal(part) !== null || (career.fabOrders ?? []).some((o) => o.salvageIn.includes(componentId))) {
    return { ok: false, reason: 'heldBack' }
  }
  if (!acceptsComponent(part.condition, opts.bands ?? SALVAGE_BANDS)) return { ok: false, reason: 'wrecked' }
  const yields = (opts.yields ?? BREAKDOWN_YIELDS)[part.type]
  let progress = k.progress
  const goods: Partial<Record<GoodId, number>> = {}
  for (const [g, n] of Object.entries(yields) as [GoodId, number | undefined][]) {
    const units = Math.floor((n ?? 0) + 1e-9)
    if (units <= 0) continue
    const r = deliverItem(k.req, progress, { kind: 'good', good: g, qty: units }, { rushFuelCellsPerPart: 0 })
    if (!r.ok) continue
    progress = r.delivered
    goods[g] = r.taken
  }
  if (Object.keys(goods).length === 0) return { ok: false, reason: 'notNeeded' }
  const next = withSite(withoutComponent(career, componentId), ref.key, { ...k.site, kit: kitOf(progress) })
  return { ok: true, career: next, goods, componentId, done: kitComplete(k.req, progress) }
}

/**
 * Abandons the open claim at `key`: `open` and the kit are cleared and the delivered goods are lost (by design: the
 * risk). The site and its survey stay. Refused when no claim is open there.
 */
export function abandon(career: Career, key: string): HulkKitResult {
  const site = liveSite(career, key)
  if (site === undefined) return { ok: false, reason: 'noSite' }
  if (site.open !== true) return { ok: false, reason: 'notOpen' }
  const next: HulkSite = { ...site }
  delete next.open
  delete next.kit
  return { ok: true, career: withSite(career, key, next) }
}

/** CH-12d's dev-spawned hulk keys (`hk:<sys>:dev<n>`): such a hulk is gone once its system is left or the game loads. */
const DEV_HULK_KEY = /^hk:[^:]+:dev\d+$/

/** True for a dev-spawned hulk key (CH-12d, `hk:<sys>:dev<n>`). */
export function isDevHulkKey(key: string): boolean {
  return DEV_HULK_KEY.test(key)
}

/**
 * PF-2: the open claims whose hulk can never come back, in key order. `systemId` is the current system and `live` the
 * keys of its hulks right now (the seeded spots and the dev hulks spawned since entry); `seededKnown` is false when
 * the seeded spots could not be placed (nothing seeded is judged then).
 *
 * - A **dev hulk** (`hk:<sys>:dev<n>`) lives only until its system is left (a save load re-enters too): orphaned
 *   unless it is in `live`.
 * - A **seeded hulk** (`hk:<sys>:<n>`) is re-placed by hash on every entry: orphaned only when it is in the current
 *   system and the placement no longer has it.
 * - An **off-screen hulk** (`ow-<seq>`) is never judged here: the worker exempts an open claim from expiry and the
 *   record cap, and the local manifest lists only the current system (the hangar's *Abandon claim* covers the rest).
 */
export function orphanedHulkClaims(career: Career, systemId: string, live: ReadonlySet<string>, seededKnown: boolean): string[] {
  const out: string[] = []
  const here = `hk:${systemId}:`
  for (const [key, site] of Object.entries(hulksOf(career).sites)) {
    if (site.open !== true || live.has(key)) continue
    if (isDevHulkKey(key) || (seededKnown && systemId !== '' && key.startsWith(here))) out.push(key)
  }
  return out.sort()
}

/** PF-2: one open claim for a list (the hangar): its key, hull and kit progress in units (delivered / needed). */
export interface OpenHulkClaim {
  readonly key: string
  readonly hull: string
  readonly delivered: number
  readonly needed: number
}

/** PF-2: every open claim on the Career, in key order, with its kit progress (UI rate). */
export function openHulkClaims(career: Career, cfg: HulksConfig = hulksConfig()): OpenHulkClaim[] {
  const out: OpenHulkClaim[] = []
  const state = hulksOf(career)
  for (const [key, site] of Object.entries(state.sites)) {
    if (site.open !== true || state.done.includes(key)) continue
    const req = hulkKitRequirement(site, cfg)
    let needed = 0
    let delivered = 0
    for (const [g, n] of Object.entries(req.goods)) {
      if (n === undefined || n <= 0) continue
      needed += n
      delivered += Math.min(n, site.kit?.goods[g as GoodId] ?? 0)
    }
    out.push({ key, hull: site.hull, delivered, needed })
  }
  return out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
}
